// VIBRANIAM-SPACE v2 - MixerSwitcherSubstrate.js
// Substrate for the Mixer/Switcher node: hard cuts between three source types
// (image strobe textures, generated backgrounds, and the live particle
// animation) at a speed that is hard-clamped to 3 Hz.

import * as THREE from 'three';

const DEFAULT_PARAMS = {
  mode: 'sequential',   // 'sequential' or 'random'
  speed: 1.0,           // Hz, hard-clamped to 3
  background: 'white',  // 'white' | 'grid' | 'circle'
  includeStrobe: true,
  includeBackground: true,
  includeAnimation: true,
};

const MAX_SPEED_HZ = 3;

export class MixerSwitcherSubstrate {
  constructor(params = {}) {
    this.params = { ...DEFAULT_PARAMS, ...params };
    this.scene = new THREE.Scene();
    this.plane = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ transparent: true })
    );
    this.scene.add(this.plane);
    this.points = this.plane;
    this.viewportWidth = 1;
    this.viewportHeight = 1;
    this.timer = 0;
    this.currentSource = 'background';
    this.strobeTextures = [];
    this.backgroundTextures = {
      white: this._solidTexture('#ffffff'),
      grid: this._gridTexture(),
      circle: this._circleTexture(),
    };
    this._sourceOrder = this._buildSourceOrder();
    this._show(this.currentSource);
  }

  _solidTexture(color) {
    const c = document.createElement('canvas');
    c.width = 2; c.height = 2;
    const ctx = c.getContext('2d');
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 2, 2);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  _gridTexture() {
    const c = document.createElement('canvas');
    c.width = 128; c.height = 128;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 128, 128);
    ctx.fillStyle = '#101020';
    for (let i = 0; i < 128; i += 16) {
      ctx.fillRect(i, 0, 1, 128);
      ctx.fillRect(0, i, 128, 1);
    }
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  _circleTexture() {
    const c = document.createElement('canvas');
    c.width = 128; c.height = 128;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, 128, 128);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(64, 64, 48, 0, Math.PI * 2);
    ctx.fill();
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  _buildSourceOrder() {
    const order = [];
    if (this.params.includeStrobe !== false) order.push('strobe');
    if (this.params.includeBackground !== false) order.push('background');
    if (this.params.includeAnimation !== false) order.push('animation');
    if (order.length === 0) order.push('background');
    return order;
  }

  setViewport(width, height) {
    this.viewportWidth = width;
    this.viewportHeight = height;
    this._updateScale();
  }

  setViewportHeight(height) {
    this.setViewport(this.viewportWidth, height);
  }

  reset() {
    this.timer = 0;
    this.currentSource = this._sourceOrder[0] || 'background';
    this._show(this.currentSource);
  }

  _updateScale() {
    if (!this.plane) return;
    // Full-viewport cover: keep the image's aspect with a cover crop.
    const tex = this.plane.material.map;
    if (tex && tex.image && tex.image.width && tex.image.height) {
      const imgAspect = tex.image.width / tex.image.height;
      const scaleX = this.viewportWidth / imgAspect;
      const scaleY = this.viewportHeight;
      const scale = Math.max(scaleX, scaleY);
      this.plane.scale.set(scale, scale, 1);
    } else {
      // Full screen for generated backgrounds.
      this.plane.scale.set(this.viewportWidth, this.viewportHeight, 1);
    }
  }

  setStrobeTextures(textures) {
    this.strobeTextures = textures || [];
  }

  setParams(params) {
    this.params = { ...this.params, ...params };
    this._sourceOrder = this._buildSourceOrder();
  }

  setAudio() {}

  _show(source) {
    this.currentSource = source;
    if (source === 'animation') {
      // The live particle field already renders into this layer: hide our
      // plane so the animation shows through (no second engine instance).
      this.plane.visible = false;
      return;
    }
    this.plane.visible = true;
    let tex = null;
    if (source === 'strobe') {
      tex = this.strobeTextures.length ? this.strobeTextures[0] : this.backgroundTextures.white;
    } else {
      tex = this.backgroundTextures[this.params.background] || this.backgroundTextures.white;
    }
    this.plane.material.map = tex;
    this.plane.material.needsUpdate = true;
    this._updateScale();
  }

  step(dt) {
    const speed = Math.min(Number(this.params.speed) || 0, MAX_SPEED_HZ);
    const interval = speed > 0 ? 1 / speed : Infinity;
    this.timer += dt;
    if (this.timer >= interval) {
      this.timer = 0;
      const order = this._sourceOrder;
      if (this.params.mode === 'random') {
        this._show(order[Math.floor(Math.random() * order.length)]);
      } else {
        const idx = order.indexOf(this.currentSource);
        const next = order[(idx + 1) % order.length];
        this._show(next);
      }
    }
  }

  dispose() {
    Object.values(this.backgroundTextures).forEach((t) => t.dispose());
    this.backgroundTextures = {};
  }
}
